import React from 'react';
import { useLifely } from '../../context/LifelyContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Loader2, LogIn, Shield } from 'lucide-react';

/**
 * AuthWrapper - Wraps protected content and handles authentication state
 * Shows login prompt when user is not authenticated
 */
const AuthWrapper = ({ children }) => {
  const { isAuthenticated, authLoading, login } = useLifely();

  // Handle opening Memberstack login modal
  const handleSignIn = async () => {
    const memberstack = window.$memberstackDom;
    
    if (!memberstack) {
      console.error('Memberstack not loaded');
      return;
    }

    try {
      // Open the login modal and wait for it to complete
      const result = await memberstack.openModal('LOGIN');
      
      // Log the ENTIRE result to see its structure
      console.log('=== FULL LOGIN RESULT ===');
      console.log(JSON.stringify(result, null, 2));
      console.log('=========================');
      
      // After successful login, get current member data
      const memberResult = await memberstack.getCurrentMember();
      console.log('=== CURRENT MEMBER ===');
      console.log(JSON.stringify(memberResult, null, 2));
      console.log('======================');
      
      // Check if we have member data
      if (memberResult?.data) {
        const member = memberResult.data;
        console.log('Member logged in:', member.id);
        console.log('Member email:', member.auth?.email);
        
        // Login to our backend with member ID
        await login(member.id);
        console.log('Backend login successful');
        
        // Close the modal if it's still open
        try {
          if (memberstack.hideModal) {
            await memberstack.hideModal();
          }
        } catch (e) {
          // Modal might already be closed
        }
      }
    } catch (err) {
      // User might have closed the modal without logging in
      if (err?.message?.includes('closed') || err?.message?.includes('cancel')) {
        console.log('Login modal was closed');
      } else {
        console.error('Login error:', err);
      }
    }
  };

  // Handle opening Memberstack signup modal
  const handleSignUp = async () => {
    const memberstack = window.$memberstackDom;
    
    if (!memberstack) {
      return;
    }

    try {
      const result = await memberstack.openModal('SIGNUP');
      console.log('Signup result:', JSON.stringify(result, null, 2));
      
      const memberResult = await memberstack.getCurrentMember();
      if (memberResult?.data) {
        await login(memberResult.data.id);
      }
    } catch (err) {
      console.error('Signup error:', err);
    }
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login prompt when not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <Card className="w-full max-w-md border-2">
          <CardHeader className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Welcome to Lifely</CardTitle>
              <CardDescription className="mt-2">
                Your calm space for mapping what matters. Sign in to access your data.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Lifely uses Memberstack for secure authentication. Click below to sign in or create an account.
            </p>
            
            <Button 
              className="w-full" 
              size="lg"
              onClick={handleSignIn}
              data-testid="sign-in-btn"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </Button>
            
            <p className="text-xs text-muted-foreground text-center">
              Don&apos;t have an account?{' '}
              <button 
                className="text-primary hover:underline"
                onClick={handleSignUp}
              >
                Create one
              </button>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is authenticated, render children
  return children;
};

export default AuthWrapper;
